import { supabase } from "@/lib/supabaseClient";
import type { Prospect } from "@/pages/prospectar/components/ProspectCard";

const platformLabel: Record<string, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  linkedin: "LinkedIn",
  whatsapp: "WhatsApp",
  facebook: "Facebook",
  other: "Otro",
};

const FALLBACK_PHOTO = "https://i.pravatar.cc/300?img=1";

type ProspectRow = {
  id: string;
  display_name: string | null;
  username: string | null;
  photo_url: string | null;
  network: string | null;
  profile_url: string | null;
  followers_count: number | null;
  niche: string | null;
  reason: string | null;
  bio: string | null;
  score: number | null;
  pipeline_stage: string | null;
  next_action: string | null;
  follow_up_label: string | null;
  suggested_opener: string | null;
  opener_approved_at: string | null;
  opener_sent_at: string | null;
  signals: { score_notes?: string[] } | null;
  track_code: string | null;
  link_clicks: number | null;
  link_registered_at: string | null;
  link_last_click_at: string | null;
  reply_count: number | null;
  last_reply_at: string | null;
  needs_human: boolean | null;
  needs_human_reason: string | null;
  handled_by: string | null;
};

function mapRow(row: ProspectRow): Prospect {
  return {
    id: row.id,
    name: row.display_name ?? row.profile_url ?? "Prospecto sin nombre",
    photo: row.photo_url ?? FALLBACK_PHOTO,
    username: row.username ?? (row.profile_url ? row.profile_url.replace(/^https?:\/\//, "") : ""),
    platform: platformLabel[row.network ?? ""] ?? "Otro",
    followers: row.followers_count ?? 0,
    niche: row.niche ?? "Sin definir",
    reason: row.reason ?? row.bio ?? "",
    score: row.score ?? 0,
    profileUrl: row.profile_url ?? "",
    scoreNotes: row.signals?.score_notes ?? undefined,
    trackCode: row.track_code ?? undefined,
    linkClicks: row.link_clicks ?? 0,
    linkRegistered: !!row.link_registered_at,
    linkLastClickAt: row.link_last_click_at ?? undefined,
    replyCount: row.reply_count ?? 0,
    lastReplyAt: row.last_reply_at ?? undefined,
    status: row.pipeline_stage ?? "Nuevo",
    nextAction: row.next_action ?? "Revisar prospecto",
    followUp: row.follow_up_label ?? "",
    needsHuman: !!row.needs_human,
    needsHumanReason: row.needs_human_reason ?? undefined,
    handledBy: row.handled_by === "ai" ? "ai" : "human",
  };
}

const SELECT_COLUMNS =
  "id, display_name, username, photo_url, network, profile_url, followers_count, niche, reason, bio, score, pipeline_stage, next_action, follow_up_label, suggested_opener, opener_approved_at, opener_sent_at, signals, track_code, link_clicks, link_registered_at, link_last_click_at, reply_count, last_reply_at, needs_human, needs_human_reason, handled_by";

/** Lista completa de prospectos visibles para el usuario autenticado (RLS ya filtra por organización / dueño). */
export async function fetchProspects(): Promise<Prospect[]> {
  const { data, error } = await supabase
    .from("prospects")
    .select(SELECT_COLUMNS)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []).map(mapRow);
}

export type ProspectOpenerState = {
  opener: string | null;
  approved: boolean;
  sent: boolean;
};

/** Igual que fetchProspects, pero además trae el estado guardado del opener (texto, aprobado, enviado). */
export async function fetchProspectsWithOpenerState(): Promise<{
  prospects: Prospect[];
  openerState: Record<string, ProspectOpenerState>;
}> {
  const { data, error } = await supabase
    .from("prospects")
    .select(SELECT_COLUMNS)
    .order("created_at", { ascending: false });

  if (error) throw error;
  const rows = (data ?? []) as ProspectRow[];
  const prospects = rows.map(mapRow);
  const openerState: Record<string, ProspectOpenerState> = {};
  for (const row of rows) {
    openerState[row.id] = {
      opener: row.suggested_opener,
      approved: !!row.opener_approved_at,
      sent: !!row.opener_sent_at,
    };
  }
  return { prospects, openerState };
}

async function getCurrentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

/**
 * Guarda el opener generado por IA en el prospecto, actualiza la próxima
 * acción sugerida (ya no tiene sentido seguir diciendo "Generar opener" una
 * vez generado) y deja registro en prospect_events. Devuelve el nuevo texto
 * de "próxima acción" para que la pantalla lo refleje sin recargar.
 */
export async function saveOpener(prospectId: string, openerText: string): Promise<{ nextAction: string }> {
  const nextAction = "Revisar y aprobar opener";
  const { error } = await supabase
    .from("prospects")
    .update({ suggested_opener: openerText, next_action: nextAction })
    .eq("id", prospectId);
  if (error) throw error;

  const actorId = await getCurrentUserId();
  await supabase.from("prospect_events").insert({
    prospect_id: prospectId,
    actor_id: actorId,
    action: "opener_generated",
    metadata: { opener: openerText },
  });

  return { nextAction };
}

/**
 * Marca el opener como aprobado: pasa el prospecto a status="approved" y
 * actualiza la próxima acción a "Enviar mensaje" (ya no hace falta seguir
 * aprobando). Devuelve el nuevo texto para reflejarlo sin recargar.
 */
export async function approveOpener(prospectId: string): Promise<{ nextAction: string }> {
  const nextAction = "Enviar mensaje";
  const { error } = await supabase
    .from("prospects")
    .update({ status: "approved", opener_approved_at: new Date().toISOString(), next_action: nextAction })
    .eq("id", prospectId);
  if (error) throw error;

  const actorId = await getCurrentUserId();
  await supabase.from("prospect_events").insert({
    prospect_id: prospectId,
    actor_id: actorId,
    action: "opener_approved",
    new_status: "approved",
  });

  return { nextAction };
}

/**
 * Marca el opener como enviado. Si la etapa del embudo seguía en "Nuevo", la
 * avanza a "Contactado". También actualiza la próxima acción y la fecha de
 * seguimiento — ya no tiene sentido que sigan diciendo "Generar opener · HOY"
 * una vez enviado el mensaje, así que pasan a reflejar que ahora toca
 * esperar respuesta. Devuelve todo lo que cambió para que la pantalla lo
 * refleje al instante, sin esperar a un recargo.
 */
export async function markOpenerSent(
  prospectId: string,
  currentStage: string
): Promise<{ pipelineStage: string; nextAction: string; followUp: string }> {
  const pipelineStage = currentStage === "Nuevo" ? "Contactado" : currentStage;
  const nextAction = "Esperando respuesta";
  const followUp = "En 2 días";
  const { error } = await supabase
    .from("prospects")
    .update({
      opener_sent_at: new Date().toISOString(),
      pipeline_stage: pipelineStage,
      next_action: nextAction,
      follow_up_label: followUp,
    })
    .eq("id", prospectId);
  if (error) throw error;

  const actorId = await getCurrentUserId();
  await supabase.from("prospect_events").insert({
    prospect_id: prospectId,
    actor_id: actorId,
    action: "opener_sent",
  });

  return { pipelineStage, nextAction, followUp };
}

export type AttentionItem = {
  id: string;
  name: string;
  photo: string;
  action: string;
  when: string;
  tone: "urgent" | "soon";
};

/** Los que necesitan atención hoy, para la tarjeta del dashboard. */
export async function fetchAttentionItems(limit = 3): Promise<AttentionItem[]> {
  const prospects = await fetchProspects();
  return prospects
    .filter((p) => p.nextAction)
    .sort((a, b) => {
      const aToday = a.followUp?.toUpperCase() === "HOY" ? 0 : 1;
      const bToday = b.followUp?.toUpperCase() === "HOY" ? 0 : 1;
      if (aToday !== bToday) return aToday - bToday;
      return b.score - a.score;
    })
    .slice(0, limit)
    .map((p) => ({
      id: p.id,
      name: p.name,
      photo: p.photo,
      action: p.nextAction,
      when: p.followUp || "Pronto",
      tone: p.followUp?.toUpperCase() === "HOY" ? "urgent" : "soon",
    }));
}

export type ProspectNetwork = "instagram" | "facebook" | "tiktok";

export type ProspectSearchResult = {
  inserted: number;
  found: number;
  duplicates?: number;
  message?: string;
  location?: string;
  /** Lista final de hashtags combinados (el escrito a mano + categorías elegidas) que devolvió el backend. */
  categories?: string[];
  error?: string;
  detail?: string;
};

export type ProspectSearchOptions = {
  location?: string;
  limit?: number;
  /** Hasta 3 categorías sugeridas elegidas con un toque, además del hashtag escrito a mano. */
  categories?: string[];
  /** Idiomas de las publicaciones a conservar ("es", "pt"). Vacío o sin definir = todos. */
  languages?: string[];
};

const MAX_SEARCH_CATEGORIES = 3;

/**
 * Dispara una búsqueda real de prospectos en redes sociales vía la función
 * "apify-prospects" (Apify) y guarda los que encuentre en la tabla
 * `prospects`. Por ahora solo "instagram" está implementado en el backend;
 * facebook/tiktok se agregan después con el mismo patrón.
 *
 * `location` es opcional: cuando se manda, el backend se queda solo con los
 * posts cuyo nombre de ubicación o descripción mencionan ese texto (filtro
 * de texto, no un filtro geográfico exacto de Instagram).
 *
 * `categories` es opcional: hasta 3 categorías sugeridas elegidas con un
 * toque en el panel, que se combinan (sin duplicados) con el hashtag escrito
 * a mano en `query`. Se puede buscar solo con categorías, sin escribir nada.
 */
export async function searchProspects(
  network: ProspectNetwork,
  query: string,
  options: ProspectSearchOptions = {}
): Promise<ProspectSearchResult> {
  const trimmed = query.trim();
  const categories = (options.categories ?? [])
    .map((c) => c.trim())
    .filter((c) => c.length > 0)
    .slice(0, MAX_SEARCH_CATEGORIES);

  if (!trimmed && categories.length === 0) {
    throw new Error("Escribe un hashtag o selecciona al menos una categoría sugerida.");
  }

  const location = options.location?.trim() || undefined;

  const { data, error } = await supabase.functions.invoke("apify-prospects", {
    body: { network, query: trimmed, location, categories, limit: options.limit ?? 20, languages: options.languages },
  });

  if (error) {
    // Si la función respondió con un error (4xx/5xx), supabase-js lo trae
    // acá en vez de en `data`; tratamos de leer el detalle si viene en el
    // body de la respuesta.
    let detail: string | undefined;
    const context = (error as { context?: Response }).context;
    if (context && typeof context.json === "function") {
      try {
        const body = await context.clone().json();
        detail = body?.error || body?.detail;
      } catch {
        // ignorar, nos quedamos con el mensaje genérico
      }
    }
    throw new Error(detail || error.message || "No se pudo completar la búsqueda.");
  }

  const result = (data ?? {}) as ProspectSearchResult;
  if (result.error) {
    throw new Error(result.detail || result.error);
  }
  return result;
}

export type ProfileSearchGeo = {
  country_code: string;
  country_name: string;
  region: string;
  city: string;
  /** Texto que se agrega a la búsqueda (ciudad, estado o país). */
  place: string;
  /** Nombres y alias con los que se reconoce el lugar en la bio de un perfil. */
  terms: string[];
  /** Solo guardar perfiles que mencionan la ubicación. */
  strict: boolean;
};

export type ProfileSearchResult = {
  geo_confirmed?: number;
  geo_filtered?: number;
  inserted: number;
  found?: number;
  duplicates?: number;
  private?: number;
  filtered?: number;
  irrelevant?: number;
  lang_filtered?: number;
  out_of_range?: number;
  message?: string;
};

const PROFILE_SEARCH_ERRORS: Record<string, string> = {
  query_too_short: "Escribe al menos 2 letras para buscar.",
  too_soon: "Espera unos segundos antes de buscar de nuevo.",
  apify_not_configured: "El buscador todavía no está activado. Avísale a tu administrador.",
  facebook_actor_missing: "Falta indicar el actor de Apify para Facebook.",
  apify_timeout: "La búsqueda tardó demasiado. Inténtalo de nuevo.",
};

/**
 * Busca perfiles de Instagram como el buscador de la red (por nombre, usuario o palabras clave),
 * sin importar si interactuaron con el socio, y los guarda como prospectos.
 */
export async function searchInstagramProfiles(query: string, limit = 20, geo?: ProfileSearchGeo): Promise<ProfileSearchResult> {
  return searchProfilesByNetwork("instagram", query, limit, geo);
}

/** Igual que el buscador de la red elegida (Instagram o TikTok), con ubicación opcional. */
export async function searchProfilesByNetwork(
  network: "instagram" | "tiktok" | "facebook",
  query: string,
  limit = 20,
  geo?: ProfileSearchGeo,
  actorId?: string,
  filters?: { min_followers?: number; max_followers?: number; languages?: string[] }
): Promise<ProfileSearchResult> {
  const { data, error } = await supabase.functions.invoke("apify-profile-prospects", {
    body: { network, query, limit, geo, actor_id: actorId, ...(filters ?? {}) },
  });
  if (error) {
    let code = "";
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === "function") {
      const body = await ctx.clone().json().catch(() => null);
      code = body?.error ?? "";
    }
    throw new Error(PROFILE_SEARCH_ERRORS[code] ?? "No se pudo completar la búsqueda. Inténtalo de nuevo.");
  }
  return (data ?? { inserted: 0 }) as ProfileSearchResult;
}

/**
 * Calcula el score (1-99) de los prospectos que todavía no lo tienen (all = true recalcula todos).
 * No gasta créditos de Apify. Devuelve cuántos se calcularon.
 */
export async function scoreProspects(all = false): Promise<number> {
  const { data, error } = await supabase.functions.invoke("score-prospects", { body: { all } });
  if (error) throw error;
  return (data as { scored?: number } | null)?.scored ?? 0;
}
